// src/api/features/users/queries.ts
import {
  useQuery,
  type QueryKey,
  type QueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import {
  listUsers,
  getUser,
  getUserModules,
} from "./api";

import type {
  UserResult,
  PaginatedUsers,
  ListUsersQuery,
} from "./types";
import type { Module } from "@/api/features/modules/types";

/* =========================
 * Query Keys
 * ========================= */

export const UsersKeys = {
  all: ["users"] as const,
  list: (params: ListUsersQuery = {}) =>
    ["users", "list", params] as const,
  detail: (id: number | string) =>
    ["users", "detail", String(id)] as const,
} as const;

export const userModulesKey = (userId: number | string) =>
  ["users", "modules", String(userId)] as const;

/* =========================
 * USERS — React Query Hooks
 * (read-only)
 * ========================= */

/** 🔹 List users with pagination + filters */
export function useUsers(
  params: ListUsersQuery = {},
  options?: { enabled?: boolean }
) {
  const key = UsersKeys.list(params);

  return useQuery<PaginatedUsers>({
    queryKey: key,
    queryFn: () => listUsers(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/** 🔹 Get a single user */
export function useUser(id: number | string | null | undefined) {
  const enabled = id !== null && id !== undefined && id !== "";
  const detailKey: QueryKey = enabled
    ? UsersKeys.detail(id as number | string)
    : (["users", "detail", "disabled"] as QueryKey);

  return useQuery<UserResult>({
    queryKey: detailKey,
    queryFn: () => getUser(id as number | string),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/** 🔹 List modules a user has access to */
export function useUserModules(
  userId: number | string | null | undefined
) {
  const enabled = userId !== null && userId !== undefined && userId !== "";
  const key: QueryKey = enabled
    ? userModulesKey(userId as number | string)
    : (["users", "modules", "disabled"] as QueryKey);

  return useQuery<Module[]>({
    queryKey: key,
    queryFn: () => getUserModules(userId as number | string),
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/* =========================
 * Prefetch helpers (optional)
 * ========================= */

export function prefetchUsers(
  qc: QueryClient,
  params: ListUsersQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: UsersKeys.list(params),
    queryFn: () => listUsers(params),
    staleTime: 30_000,
  });
}

export function prefetchUser(
  qc: QueryClient,
  id: number | string
) {
  return qc.prefetchQuery({
    queryKey: UsersKeys.detail(id),
    queryFn: () => getUser(id),
    staleTime: 60_000,
  });
}

export function prefetchUserModules(
  qc: QueryClient,
  userId: number | string
) {
  return qc.prefetchQuery({
    queryKey: userModulesKey(userId),
    queryFn: () => getUserModules(userId),
    staleTime: 30_000,
  });
}
